/**
 * `/api/partner/*` client.
 *
 * `credentials: "include"` on every call — the partner session is an httpOnly
 * cookie (`partner.session`) set by verify-otp, and without this the browser
 * drops it on a cross-origin dev setup (portal on 7009, API on 7001). The API
 * side needs the partner origin in ALLOWED_ORIGINS for the same reason.
 */

import type {
	Partner,
	PartnerMe,
	Payout,
	Referral,
	ReferralDetail,
	SessionResponse,
} from "./types";

const BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:7001";

/** A non-2xx answer, with the server's own `error` code kept intact. */
export class ApiError extends Error {
	readonly status: number;
	readonly code: string;
	readonly path: string;
	readonly body: Record<string, unknown>;

	constructor(status: number, path: string, body: Record<string, unknown>) {
		const code = typeof body.error === "string" ? body.error : "request_failed";
		super(code);
		this.name = "ApiError";
		this.status = status;
		this.path = path;
		this.code = code;
		this.body = body;
	}

	/**
	 * A 401 here means the session is gone — but only away from the auth
	 * endpoints, where 401 means "wrong code" and must not bounce the partner
	 * off the screen they are typing it into. 404 `not_found` is the DPDP
	 * erasure case: a valid cookie for a prospect that no longer exists, which
	 * the API asks the client to treat as a sign-out rather than a retry.
	 */
	get isSessionGone(): boolean {
		if (this.path.startsWith("/api/partner/auth/")) return false;
		return this.status === 401 || this.code === "not_found";
	}

	/** 422 below_threshold carries how much more is needed. */
	get shortfallPaise(): number | null {
		return typeof this.body.shortfallPaise === "number"
			? this.body.shortfallPaise
			: null;
	}
}

async function request<T>(
	path: string,
	body?: unknown,
	// GET with no body, POST with one — what every existing caller relies on.
	// Named only by a route that needs another verb.
	method: "GET" | "POST" | "PATCH" = body === undefined ? "GET" : "POST",
): Promise<T> {
	let response: Response;
	try {
		response = await fetch(`${BASE_URL}${path}`, {
			method,
			credentials: "include",
			headers: body === undefined ? {} : { "Content-Type": "application/json" },
			body: body === undefined ? undefined : JSON.stringify(body),
		});
	} catch {
		throw new ApiError(0, path, { error: "network_error" });
	}

	const parsed = await response.json().catch(() => ({}));
	const payload = (
		parsed && typeof parsed === "object" ? parsed : {}
	) as Record<string, unknown>;

	if (!response.ok) throw new ApiError(response.status, path, payload);
	return payload as T;
}

export const api = {
	/** Always resolves `{ok:true}` — never reveals whether the number is known. */
	requestOtp: (phone: string) =>
		request<{ ok: true }>("/api/partner/auth/request-otp", { phone }),

	/**
	 * Signs in AND registers, through one endpoint.
	 *
	 * The client cannot know which it is doing, and must not be able to find
	 * out — an endpoint that answered "is this number registered?" would be an
	 * oracle on our partner list. So it sends the code alone, and a 409
	 * `registration_required` is how it learns to ask for a name. The partner
	 * is never made to work out which flow they are in.
	 *
	 * `consentVersion` names the consent line the registration screen showed.
	 * The API stores it against the account, and refuses to create one without
	 * it unless the number already consented on the go form — which is the
	 * other way a 409 comes back.
	 */
	verifyOtp: (
		phone: string,
		code: string,
		profile?: {
			name: string;
			firmName?: string;
			closingsBand?: string;
			consentVersion?: string;
		},
	) =>
		request<{ ok: true; name: string; registered: boolean }>(
			"/api/partner/auth/verify-otp",
			{ phone, code, ...(profile ?? {}) },
		),

	logout: () => request<{ ok: true }>("/api/partner/auth/logout", {}),

	session: () => request<SessionResponse>("/api/partner/auth/session"),

	me: () => request<PartnerMe>("/api/partner/me"),

	/**
	 * The two things a partner may change about themselves. Only the keys
	 * given are written. The PAN goes over in full and comes back as `hasPan`:
	 * the API keeps its last four and nothing else (FR-M-7).
	 */
	updateMe: (body: { language?: Partner["language"]; pan?: string }) =>
		request<{ partner: Partner }>("/api/partner/me", body, "PATCH"),

	requestPayout: (upiId: string) =>
		request<Payout>("/api/partner/payouts/request", { upiId }),

	listReferrals: () =>
		request<{ referrals: Referral[] }>("/api/partner/referrals"),

	referral: (id: string) =>
		request<ReferralDetail>(`/api/partner/referrals/${id}`),

	/**
	 * Creates a `draft` and sends NOTHING to anyone. The returned
	 * `whatsappUrl` opens the partner's own WhatsApp — they forward it.
	 */
	createReferral: (input: {
		contactName: string;
		contactPhone: string;
		society?: string;
		config?: string;
		/** `YYYY-MM`. The API derives the possession band from it. */
		possessionMonth?: string;
		/** The partner confirmed they may share this number (#1085). */
		consentAsserted: boolean;
	}) =>
		request<{
			referral: Referral;
			whatsappUrl: string;
			duplicate: boolean;
		}>("/api/partner/referrals", input),

	/** Idempotent: a second call returns the referral unchanged. */
	markForwarded: (id: string) =>
		request<{ referral: Referral }>(
			`/api/partner/referrals/${id}/forwarded`,
			{},
		),

	/** Idempotent — mints the partner's share code on first call. */
	shareLink: () =>
		request<{ code: string; url: string }>("/api/partner/share/link", {}),

	nudge: (id: string) =>
		request<{ whatsappUrl: string; nudgeCount: number; remaining: number }>(
			`/api/partner/referrals/${id}/nudge`,
			{},
		),
};

/**
 * Shared query descriptors — one definition each so the route guard, the
 * dashboard and the payout screen read the same cache entry instead of
 * drifting apart on a key.
 */
export const sessionQuery = {
	queryKey: ["partner", "session"] as const,
	queryFn: api.session,
	retry: false,
	staleTime: 30_000,
};

export const meQuery = {
	queryKey: ["partner", "me"] as const,
	queryFn: api.me,
	retry: false,
};
